import Link from '@/components/utilities/SmartLink';
import VisibleLoading from '@/components/utilities/VisibleLoading';

const FeaturesCardsContainer = ({ cards }) => {
  return (
    <div className="mx-auto mb-6 flex w-[90vw] flex-wrap gap-6 lg:mt-32 xl:w-(--container-width)">
      {(cards || []).map((card, index) => (
        <FeaturesCard index={index} key={card.id} card={card} />
      ))}
    </div>
  );
};
export default FeaturesCardsContainer;

const SIZES = {
  full: 'basis-full',
  small: 'basis-[496px]',
  large: 'basis-[calc(var(--container-width)_-_496px_-_24px)]'
};

const STYLES = {
  green: {
    color: 'text-green-500',
    fill: 'fill-green-500',
    border: 'group-hover:border-green-500',
    shadow: null,
    graphic: (
      <>
        <svg width="383" height="362" viewBox="0 0 383 362" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle className="stroke-green-500" opacity="0.1" cx="431" cy="-69" r="342.5" />
          <circle className="stroke-green-500" opacity="0.07" cx="431" cy="-69" r="430.5" />
          <circle className="stroke-green-500" opacity="0.25" cx="431" cy="-69" r="256.5" />
          <circle className="stroke-green-500" opacity="0.5" cx="431" cy="-69" r="174.5" />
        </svg>

        <MessageIcon
          centerX={-48}
          centerY={-69}
          size={38}
          iconSize={22}
          className="[transform:rotate(328deg)_translate(-340px,0px)] bg-[#E6F9F1] transition-transform duration-2000 group-hover:[transform:rotate(290deg)_translate(-340px,0px)]"
          iconClassName="[transform:rotate(32deg)] transition-transform duration-2000 group-hover:[transform:rotate(70deg)]"
        />

        <MessageIcon
          centerX={-48}
          centerY={-69}
          size={38}
          iconSize={22}
          className="[transform:rotate(335deg)_translate(-170px,0px)] bg-[#9CE7C8] transition-transform duration-2000 group-hover:[transform:rotate(310deg)_translate(-170px,0px)]"
          iconClassName="[transform:rotate(25deg)] transition-transform duration-2000 group-hover:[transform:rotate(50deg)]"
        />

        <MessageIcon
          centerX={-48}
          centerY={-69}
          size={38}
          iconSize={22}
          className="[transform:rotate(277deg)_translate(-430px,0px)] bg-[#EEFBF5] transition-transform duration-2500 group-hover:[transform:rotate(300deg)_translate(-430px,0px)]"
          iconClassName="[transform:rotate(83deg)] transition-transform duration-2500 group-hover:[transform:rotate(60deg)]"
        />

        <MessageIcon
          centerX={-48}
          centerY={-69}
          size={64}
          iconSize={38}
          className="[transform:rotate(300deg)_translate(-255px,0px)] bg-green-500 transition-transform duration-2000 group-hover:[transform:rotate(320deg)_translate(-255px,0px)]"
          iconClassName="[transform:rotate(60deg)] transition-transform duration-2000 group-hover:[transform:rotate(40deg)]"
        />
      </>
    )
  },
  blue: {
    color: 'text-blue-500',
    fill: 'fill-blue-500',
    border: 'group-hover:border-blue-500',
    shadow: (
      <div className="absolute right-0 -bottom-40 z-1 h-[494px] w-[500px] transform-gpu rounded-full bg-blue-500 opacity-30 blur-[150px]" />
    ),
    graphic: (
      <>
        <svg width="396" height="398" viewBox="0 0 396 398" fill="none" xmlns="http://www.w3.org/2000/svg">
          <line className="stroke-blue-500" opacity="0.07" x1="401.406" y1="396.848" x2="0.646446" y2="-3.91158" />
          <line className="stroke-blue-500" opacity="0.1" x1="452.317" y1="345.936" x2="51.5581" y2="-54.8232" />
          <line className="stroke-blue-500" opacity="0.25" x1="503.229" y1="295.024" x2="102.47" y2="-105.735" />
          <line className="stroke-blue-500" opacity="0.5" x1="554.141" y1="244.113" x2="153.381" y2="-156.646" />
        </svg>

        <MessageIcon
          centerX={0}
          centerY={0}
          size={38}
          iconSize={22}
          className="[transform:translate(-260px,20px)_rotate(47deg)_translate(300px,0px)] bg-[#EBEFFF] transition-transform duration-2000 group-hover:[transform:translate(-260px,20px)_rotate(47deg)_translate(60px,0px)]"
          iconClassName="[transform:rotate(-47deg)]"
        />

        <MessageIcon
          centerX={0}
          centerY={0}
          size={64}
          iconSize={38}
          className="[transform:translate(-260px,20px)_rotate(47deg)_translate(40px,0px)] bg-blue-500 transition-transform duration-2000 group-hover:[transform:translate(-260px,20px)_rotate(47deg)_translate(200px,0px)]"
          iconClassName="[transform:rotate(-47deg)]"
        />

        <MessageIcon
          centerX={0}
          centerY={0}
          size={38}
          iconSize={22}
          className="[transform:translate(-30px,50px)_rotate(47deg)_translate(40px,0px)] bg-[#9CB1FF] transition-transform duration-2000 group-hover:[transform:translate(-30px,50px)_rotate(47deg)_translate(-60px,0px)]"
          iconClassName="[transform:rotate(-47deg)]"
        />
      </>
    )
  },
  brand: {
    color: 'text-brand-500',
    fill: 'fill-brand-500',
    border: 'group-hover:border-brand-500',
    shadow: (
      <div className="bg-brand-500 absolute -right-30 -bottom-40 z-1 h-[716px] w-[726px] transform-gpu rounded-full opacity-15 blur-[150px]" />
    ),
    graphic: (
      <>
        <svg
          className="translate-y-2"
          width="419"
          height="161"
          viewBox="0 0 419 161"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <line
            opacity="0.1"
            x1="566.759"
            y1="160.5"
            x2="2.18557e-08"
            y2="160.5"
            stroke="url(#paint0_linear_10814_45045)"
          />
          <line
            opacity="0.25"
            x1="566.759"
            y1="120.5"
            x2="2.18557e-08"
            y2="120.5"
            stroke="url(#paint1_linear_10814_45045)"
          />
          <line
            opacity="0.5"
            x1="566.759"
            y1="80.5"
            x2="2.18557e-08"
            y2="80.5"
            stroke="url(#paint2_linear_10814_45045)"
          />
          <line
            opacity="0.25"
            x1="566.759"
            y1="40.5"
            x2="2.18557e-08"
            y2="40.5"
            stroke="url(#paint3_linear_10814_45045)"
          />
          <line
            opacity="0.1"
            x1="566.759"
            y1="0.5"
            x2="2.18557e-08"
            y2="0.500025"
            stroke="url(#paint4_linear_10814_45045)"
          />
          <defs>
            <linearGradient
              id="paint0_linear_10814_45045"
              x1="-2.18556e-08"
              y1="159.5"
              x2="566.759"
              y2="159.5"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0.1" stopColor="var(--color-brand-500)" stopOpacity="0" />
              <stop offset="0.3" stopColor="var(--color-brand-500)" />
            </linearGradient>
            <linearGradient
              id="paint1_linear_10814_45045"
              x1="-2.18556e-08"
              y1="119.5"
              x2="566.759"
              y2="119.5"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="var(--color-brand-500)" stopOpacity="0" />
              <stop offset="0.2" stopColor="var(--color-brand-500)" />
            </linearGradient>
            <linearGradient
              id="paint2_linear_10814_45045"
              x1="-2.18556e-08"
              y1="79.5"
              x2="566.759"
              y2="79.5"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="var(--color-brand-500)" stopOpacity="0" />
              <stop offset="0.2" stopColor="var(--color-brand-500)" />
            </linearGradient>
            <linearGradient
              id="paint3_linear_10814_45045"
              x1="-2.18556e-08"
              y1="39.5"
              x2="566.759"
              y2="39.5"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="var(--color-brand-500)" stopOpacity="0" />
              <stop offset="0.2" stopColor="var(--color-brand-500)" />
            </linearGradient>
            <linearGradient
              id="paint4_linear_10814_45045"
              x1="0"
              y1="-0.499975"
              x2="352.001"
              y2="-0.499975"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="var(--color-brand-500)" stopOpacity="0" />
              <stop offset="0.198969" stopColor="var(--color-brand-500)" />
            </linearGradient>
          </defs>
        </svg>

        <PlusIcon
          centerX={0}
          centerY={0}
          size={48}
          iconSize={20}
          className="bg-brand-500 [transform:translate(-50px,88px)] transition-transform duration-2000 group-hover:[transform:translate(-250px,88px)]"
        />

        <PlusIcon
          centerX={0}
          centerY={0}
          size={24}
          iconSize={10}
          className="[transform:translate(-200px,8px)] bg-[#FF9F88] transition-transform duration-2000 group-hover:[transform:translate(-100px,8px)]"
        />
      </>
    )
  },
  yellow: {
    color: 'text-yellow-500',
    fill: 'fill-yellow-500',
    border: 'group-hover:border-yellow-500',
    shadow: (
      <div className="absolute -top-20 -left-20 z-1 h-[394px] w-[403px] transform-gpu rounded-full bg-yellow-300 opacity-30 blur-[162px]" />
    ),
    graphic: (
      <>
        <svg width="252" height="231" viewBox="0 0 252 231" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle className="stroke-yellow-500" opacity="0.1" cx="255" cy="-24" r="202.435" />
          <circle className="stroke-yellow-500" opacity="0.25" cx="255" cy="-24" r="151.553" />
          <circle className="stroke-yellow-500" opacity="0.5" cx="255" cy="-24" r="103.038" />
        </svg>

        <PlusIcon
          centerX={-3}
          centerY={-24}
          size={48}
          iconSize={20}
          className="[transform:rotate(338deg)_translate(-100px,0px)] bg-yellow-500 transition-transform duration-2000 group-hover:[transform:rotate(290deg)_translate(-100px,0px)]"
          iconClassName="[transform:rotate(22deg)] transition-transform duration-2000 group-hover:[transform:rotate(70deg)]"
        />

        <PlusIcon
          centerX={-3}
          centerY={-24}
          size={24}
          iconSize={10}
          className="[transform:rotate(280deg)_translate(-150px,0px)] bg-[#FFEDC0] transition-transform duration-2000 group-hover:[transform:rotate(330deg)_translate(-150px,0px)]"
          iconClassName="[transform:rotate(80deg)] transition-transform duration-2000 group-hover:[transform:rotate(30deg)]"
        />
      </>
    )
  },
  purple: {
    color: 'text-purple-500',
    fill: 'fill-purple-500',
    border: 'group-hover:border-purple-500',
    shadow: (
      <div className="absolute -top-20 -left-20 z-1 h-[394px] w-[403px] transform-gpu rounded-full bg-yellow-300 opacity-30 blur-[162px]" />
    ),
    graphic: (
      <>
        <svg width="252" height="231" viewBox="0 0 252 231" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle className="stroke-purple-500" opacity="0.1" cx="255" cy="-24" r="202.435" />
          <circle className="stroke-purple-500" opacity="0.25" cx="255" cy="-24" r="151.553" />
          <circle className="stroke-purple-500" opacity="0.5" cx="255" cy="-24" r="103.038" />
        </svg>

        <PlusIcon
          centerX={-3}
          centerY={-24}
          size={48}
          iconSize={20}
          className="[transform:rotate(338deg)_translate(-100px,0px)] bg-purple-500 transition-transform duration-2000 group-hover:[transform:rotate(290deg)_translate(-100px,0px)]"
          iconClassName="[transform:rotate(22deg)] transition-transform duration-2000 group-hover:[transform:rotate(70deg)]"
        />

        <PlusIcon
          centerX={-3}
          centerY={-24}
          size={24}
          iconSize={10}
          className="[transform:rotate(280deg)_translate(-150px,0px)] bg-purple-200 transition-transform duration-2000 group-hover:[transform:rotate(330deg)_translate(-150px,0px)]"
          iconClassName="[transform:rotate(80deg)] transition-transform duration-2000 group-hover:[transform:rotate(30deg)]"
        />
      </>
    )
  }
};

const FeaturesCard = ({ card, index }) => {
  const flexBasis = SIZES[card.size] || SIZES.full;
  const { fill, border, shadow, graphic } = STYLES[card.color] || STYLES.brand;

  const content = (
    <VisibleLoading key={card.id} delay={((index % 2) + 1) * 100}>
      {({ ref, className }) => (
        <div
          ref={ref}
          className={`relative z-2 flex h-full min-h-[300px] flex-col justify-between overflow-hidden rounded-3xl bg-white p-6 shadow-2xl/15 xl:p-8 ${className || ''}`.trim()}
        >
          {graphic && (
            <div className="pointer-events-none absolute top-0 right-0 -z-1 origin-top-right scale-70 xl:scale-100">
              {graphic}
            </div>
          )}

          <div className="flex flex-1 flex-col justify-between">
            <h3 className="heading-4 mt-12 mb-4 max-w-[430px]">{card.title}</h3>
            <div className="txt-lg mb-6 max-w-[430px]">{card.description}</div>
          </div>

          {card.url ? (
            <div
              className={`relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border border-black duration-500 ${border}`}
            >
              <ArrowIcon
                className="absolute -translate-x-7 transition-transform duration-500 group-hover:translate-x-0"
                classNameFill={fill}
              />
              <ArrowIcon className="transition-transform duration-500 group-hover:translate-x-7" />
            </div>
          ) : (
            <div className="h-10 w-10" />
          )}
        </div>
      )}
    </VisibleLoading>
  );

  return (
    <div className={`group relative ${card.url ? 'cursor-pointer' : ''} ${flexBasis} rounded-3xl`}>
      {card.url ? <Link href={card.url}>{content}</Link> : <div className="inline">{content}</div>}

      {card.shadow ? (
        <VisibleLoading delay={500}>
          {({ ref, className }) => (
            <div ref={ref} className={className}>
              {shadow}
            </div>
          )}
        </VisibleLoading>
      ) : null}
    </div>
  );
};

function ArrowIcon({ className, classNameFill }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M10 3.3335L8.82501 4.5085L13.475 9.16683H3.33334V10.8335H13.475L8.82501 15.4918L10 16.6668L16.6667 10.0002L10 3.3335Z"
        fill="black"
        className={classNameFill}
      />
    </svg>
  );
}

function MessageIcon({ centerX, centerY, size, iconSize, className, iconClassName }) {
  const top = centerY - size / 2;
  const right = centerX - size / 2;

  const style = {
    top: `${top}px`,
    right: `${right}px`,
    height: `${size}px`,
    width: `${size}px`
  };

  return (
    <div
      className={`pointer-events-none absolute -z-1 flex origin-center items-center justify-center rounded-full ${className}`}
      style={style}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={iconSize}
        height={iconSize}
        viewBox="0 0 26 26"
        fill="none"
        className={iconClassName}
      >
        <path
          d="M18.4164 22.2087H7.58305C4.33305 22.2087 2.16638 20.5837 2.16638 16.792V9.20866C2.16638 5.41699 4.33305 3.79199 7.58305 3.79199H18.4164C21.6664 3.79199 23.833 5.41699 23.833 9.20866V16.792C23.833 20.5837 21.6664 22.2087 18.4164 22.2087Z"
          stroke="white"
          strokeWidth="1.5"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M18.417 9.75L15.0261 12.4583C13.9103 13.3467 12.0794 13.3467 10.9636 12.4583L7.58362 9.75"
          stroke="white"
          strokeWidth="1.5"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

function PlusIcon({ centerX, centerY, size, iconSize, className, iconClassName }) {
  const top = centerY - size / 2;
  const right = centerX - size / 2;

  const style = {
    top: `${top}px`,
    right: `${right}px`,
    height: `${size}px`,
    width: `${size}px`
  };

  return (
    <div
      className={`pointer-events-none absolute -z-1 flex origin-center items-center justify-center rounded-full ${className}`}
      style={style}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={iconSize}
        height={iconSize}
        viewBox="0 0 21 21"
        fill="none"
        className={iconClassName}
      >
        <path d="M11.2403 11.3228H9.03516V20.2762H11.2403V11.3228Z" fill="white" />
        <path d="M11.2364 0.25H9.03125V9.20345H11.2364V0.25Z" fill="white" />
        <path d="M20.0227 9.14502H11.2031V11.3836H20.0227V9.14502Z" fill="white" />
        <path d="M9.06959 9.14502H0.25V11.3836H9.06959V9.14502Z" fill="white" />
      </svg>
    </div>
  );
}
